import { useEffect, useRef, useCallback, useState } from 'react';

interface AutoSaveOptions {
  key: string;
  data: any;
  delay?: number;
  enabled?: boolean;
}

export const useAutoSave = ({ key, data, delay = 2000, enabled = true }: AutoSaveOptions) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const savedDataRef = useRef<string>('');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(() => {
    const ts = localStorage.getItem(`autosave_${key}_timestamp`);
    return ts ? new Date(parseInt(ts)) : null;
  });

  const saveToStorage = useCallback((dataToSave: any) => {
    try {
      const serializedData = JSON.stringify(dataToSave);
      if (serializedData !== savedDataRef.current) {
        const now = Date.now();
        localStorage.setItem(`autosave_${key}`, serializedData);
        localStorage.setItem(`autosave_${key}_timestamp`, now.toString());
        savedDataRef.current = serializedData;
        setLastSavedAt(new Date(now));
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [key]);

  const loadFromStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(`autosave_${key}`);
      const timestamp = localStorage.getItem(`autosave_${key}_timestamp`);
      
      if (saved && timestamp) {
        const ageInHours = (Date.now() - parseInt(timestamp)) / (1000 * 60 * 60);
        // Only load if data is less than 24 hours old
        if (ageInHours < 24) {
          return JSON.parse(saved);
        } else {
          // Clean up old data
          clearSavedData();
        }
      }
    } catch (error) {
      console.error('Auto-load failed:', error);
    }
    return null;
  }, [key]);

  const clearSavedData = useCallback(() => {
    localStorage.removeItem(`autosave_${key}`);
    localStorage.removeItem(`autosave_${key}_timestamp`);
    savedDataRef.current = '';
    setLastSavedAt(null);
  }, [key]);

  const getSavedDataAge = useCallback(() => {
    const timestamp = localStorage.getItem(`autosave_${key}_timestamp`);
    if (timestamp) {
      return new Date(parseInt(timestamp));
    }
    return null;
  }, [key]);

  // Auto-save with debounce
  useEffect(() => {
    if (!enabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      saveToStorage(data);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, delay, enabled, saveToStorage]);

  return {
    loadFromStorage,
    clearSavedData,
    getSavedDataAge,
    lastSavedAt,
    manualSave: () => saveToStorage(data),
  };
};
